import ValidationSchemaBuilder from 'utils/ValidationSchemaBuilder'
import * as yup from 'yup'
import {
  JOB_TITLES,
  EDUCATION_TYPES,
  WORK_LOCATION_TYPES,
  EMPLOYMENT_TYPES,
  AREA_OF_EXPERTISE,
  YEARS_OF_WORK_EXPERIENCE,
} from 'constants/user'

const schemaBuilder = new ValidationSchemaBuilder()

export const myProfileSchema = schemaBuilder
  .firstName()
  .lastName()
  .email()
  .mobileContactNumber()
  .workPhoneNumber()
  .custom({
    // Job title validation
    jobTitle: yup
      .string()
      .required('Please select a job title')
      .oneOf([...JOB_TITLES.map((job) => job.value)], 'Please select a valid job title'),
    customJobTitle: yup.string().when('jobTitle', {
      is: 'Other',
      then: (schema) =>
        schema
          .required('Please enter your job title')
          .min(2, 'Job title must be at least 2 characters')
          .max(100, 'Job title must be less than 100 characters'),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Education validation
    education: yup
      .string()
      .required('Please select your education level')
      .oneOf([...EDUCATION_TYPES.map((edu) => edu.value)], 'Please select a valid education level'),
    customEducation: yup.string().when('education', {
      is: 'Other',
      then: (schema) =>
        schema
          .required('Please enter your education level')
          .min(2, 'Education level must be at least 2 characters')
          .max(100, 'Education level must be less than 100 characters'),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Work location validation
    workLocation: yup
      .string()
      .required('Please select your work location')
      .oneOf(
        [...WORK_LOCATION_TYPES.map((loc) => loc.value)],
        'Please select a valid work location'
      ),
    customWorkLocation: yup.string().when('workLocation', {
      is: 'Other',
      then: (schema) =>
        schema
          .required('Please enter your work location')
          .min(2, 'Work location must be at least 2 characters')
          .max(100, 'Work location must be less than 100 characters'),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Employment type validation
    employmentType: yup
      .string()
      .required('Please select your employment type')
      .oneOf(
        [...EMPLOYMENT_TYPES.map((emp) => emp.value)],
        'Please select a valid employment type'
      ),
    customEmploymentType: yup.string().when('employmentType', {
      is: 'Other',
      then: (schema) =>
        schema
          .required('Please enter your employment type')
          .min(2, 'Employment type must be at least 2 characters')
          .max(100, 'Employment type must be less than 100 characters'),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Expertise validation
    expertise: yup
      .string()
      .required('Please select at least one area of expertise')
      .test('expertise-required', 'Please select at least one area of expertise', function (value) {
        if (!value || value.trim() === '') return false
        const expertiseArray = value
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item)
        return expertiseArray.length > 0
      }),
    customExpertise: yup
      .string()
      .test(
        'custom-expertise-validation',
        'Please enter valid areas of expertise',
        function (value) {
          const { expertise } = this.parent
          if (!expertise) return true

          const expertiseArray = expertise
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item)
          const hasOther = expertiseArray.includes('Other')
          const hasCustomValues = expertiseArray.some(
            (exp) => !AREA_OF_EXPERTISE.map((option) => option.value).includes(exp)
          )

          if (hasOther || hasCustomValues) {
            if (!value || value.trim() === '') {
              return this.createError({ message: 'Please enter your areas of expertise' })
            }

            const customValues = value
              .split(',')
              .map((item) => item.trim())
              .filter((item) => item)
            if (customValues.length === 0) {
              return this.createError({ message: 'Please enter at least one area of expertise' })
            }

            // Validate each custom expertise entry
            for (const customValue of customValues) {
              if (customValue.length < 2) {
                return this.createError({
                  message: 'Each area of expertise must be at least 2 characters',
                })
              }
              if (customValue.length > 50) {
                return this.createError({
                  message: 'Each area of expertise must be less than 50 characters',
                })
              }
            }
          }

          return true
        }
      ),

    // Work experience validation
    workExperience: yup
      .string()
      .required('Please select your years of work experience')
      .oneOf(
        [...YEARS_OF_WORK_EXPERIENCE.map((exp) => exp.value)],
        'Please select a valid work experience range'
      ),

    // LinkedIn profile validation - must be a valid LinkedIn handle (3-100 chars, alphanumeric, hyphens, no word "linkedin")
    // Required unless user checks "I don't have one"
    linkedinProfile: yup.string().when('hasLinkedin', {
      is: true,
      then: (schema) =>
        schema.test(
          'linkedin-handle-validation',
          'Please enter a valid handle (letters, numbers, and hyphens only). Example: janesmith or john-dansa',
          function (value) {
            const { path, createError } = this

            // If empty, show the "required" error message
            if (!value || value.trim() === '') {
              return createError({
                path,
                message: 'Please enter your LinkedIn handle or check "I don\'t have one"',
              })
            }

            const trimmedValue = value.trim()

            // Check length (3-100 characters)
            if (trimmedValue.length < 3 || trimmedValue.length > 100) {
              return false
            }

            // Check for valid characters (alphanumeric and hyphens only, no spaces or special characters)
            if (!/^[a-zA-Z0-9-]+$/.test(trimmedValue)) {
              return false
            }

            // Check if it contains the word "linkedin" (case insensitive)
            if (/linkedin/i.test(trimmedValue)) {
              return false
            }

            return true
          }
        ),
      otherwise: (schema) => schema.notRequired(),
    }),

    hasLinkedin: yup.boolean().default(true),

    // Country validation
    country: yup
      .string()
      .required('Please select your country')
      .min(2, 'Please select a valid country')
      .max(2, 'Please select a valid country'),

    // Work address validation
    workAddress: yup
      .string()
      .test('work-address-validation', 'Please enter a valid work address', function (value) {
        if (!value || value.trim() === '') return true // Optional field

        if (value.length < 5) {
          return this.createError({ message: 'Work address must be at least 5 characters' })
        }

        if (value.length > 200) {
          return this.createError({ message: 'Work address must be less than 200 characters' })
        }

        return true
      }),

    // SMS enabled validation
    isSmsEnabled: yup.boolean(),
  })
  .getSchema()

export const profileSchema = schemaBuilder
  .firstName()
  .lastName()
  .email()
  .mobileContactNumber()
  .getSchema()

export const businessSchema = schemaBuilder.businessName().getSchema()
export const businessOtherDetailSchema = schemaBuilder
  .custom({
    businessVision: yup.string().required('You must Enter business Vision'),
    challenges: yup.string().required('You must Enter challenges'),
  })
  .getSchema()

export const availabilityHoursSchema = schemaBuilder
  .custom({
    duration: yup.number().required('Please select duration of the meeting'),
    timeZone: yup.string().required(),
    timeSlots: yup.array().of(
      yup.object({
        lable: yup.string(),
        value: yup.string(),
        slots: yup.array().of(
          yup.object({
            startTime: yup.string().required('Please select the starting Time'),
            endTime: yup.string().required('Please select the end time'),
          })
        ),
      })
    ),
  })
  .getSchema()

export const monthlyAttestationSchema = schemaBuilder
  .fullTimeEmployees()
  .partTimeEmployees()
  .contractors()
  .volunteers()
  .jobsCreated()
  .revenue()
  .wages()
  .debtFinancing()
  .businessBankAccount()
  .newCustomer()
  .productSale()
  .serviceSale()
  .targetAudience()
  .physicalLocation()
  .financialGrowth()
  .getSchema()
export const clientDetailsBusinessSchema = schemaBuilder
  .month()
  .year()
  .businessEmail()
  .businessName()
  .clientDetailsFullTimeEmployees()
  .clientDetailsPartTimeEmployees()
  .clientDetailsContractors()
  .clientDetailsVolunteers()
  .facebook()
  .instagram()
  .linkedin()
  .twitter()
  .tiktok()
  .youtube()
  .domainName()
  .hasFacebook()
  .hasTikTok()
  .hasLinkedin()
  .hasInstagram()
  .hasYoutube()
  .hasTwitter()
  .businessAddress()
  .registeredBusinessStructure()
  .otherRegisteredBusinessStructure()
  .hasRegisterBusinessStructure()
  .businessEINCode()
  .hasEIN()
  .yearEstablished()
  .hasWebsite()
  .getSchema()

export const AccountBusinessSchema = schemaBuilder
  .ein()
  .year()
  .month()
  .naicsCode()
  .businessName()
  .businessEmail()
  .businessWebsite()
  .clientDetailsVolunteers()
  .clientDetailsContractors()
  .clientDetailsFullTimeEmployees()
  .clientDetailsPartTimeEmployees()
  .totalEmployees()
  .getSchema()

export const subscribeToSmsSchema = schemaBuilder.isSmsOptedIn().getSchema()
